import { useState } from 'react';
import { Plus, Search, Pencil, MapPin } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Spinner, EmptyState } from '@/shared/ui/Spinner';
import { PhotoPolicyBadge } from '@/shared/ui/PhotoPolicyBadge';
import { useObjects } from './objects.api';
import { ObjectFormDialog } from './ObjectFormDialog';
import { useCustomers } from '@/features/customers/customers.api';
import type { SiteObject } from '@/shared/api/types';

export function ObjectsPage() {
  const [q, setQ] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editObject, setEditObject] = useState<SiteObject | null>(null);

  const { data, isLoading } = useObjects({ q, customerId: customerId || undefined, activeOnly });
  const { data: customers } = useCustomers('');

  const openNew = () => {
    setEditObject(null);
    setFormOpen(true);
  };
  const openEdit = (o: SiteObject) => {
    setEditObject(o);
    setFormOpen(true);
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Objekte</h1>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4" /> Neues Objekt
        </Button>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="sm:w-48">
          <option value="">Alle Kunden</option>
          {customers?.items.map((c) => (
            <option key={c.id} value={c.id}>
              {c.companyName}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-2 whitespace-nowrap text-sm text-slate-600">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="h-4 w-4 rounded"
          />
          Nur aktive
        </label>
      </div>

      {isLoading ? (
        <Spinner />
      ) : !data || data.items.length === 0 ? (
        <EmptyState title="Keine Objekte" hint="Legen Sie ein Objekt für einen Kunden an." />
      ) : (
        <ul className="space-y-2">
          {data.items.map((o) => (
            <li
              key={o.id}
              className={`flex items-start justify-between rounded-lg border bg-white px-4 py-3 hover:shadow-sm ${
                o.active ? 'border-slate-200' : 'border-slate-200 opacity-60'
              }`}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-slate-900">{o.name}</p>
                  <PhotoPolicyBadge policy={o.photoPolicy} />
                  {!o.active && (
                    <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-500">
                      inaktiv
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
                  {o.customer && <span>{o.customer.companyName}</span>}
                  {o.address && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {o.address}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => openEdit(o)}
                className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <Pencil className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {data && <p className="mt-3 text-xs text-slate-400">{data.total} Objekte</p>}

      <ObjectFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        object={editObject}
        presetCustomerId={customerId || undefined}
      />
    </div>
  );
}
